// Per-project permissions admin page.

import { useState } from 'react';
import { Plus, Trash2, Users, KeyRound } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardFooter, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { useProjects } from '../../api/client';
import {
  usePermissions,
  createPermission,
  deletePermission,
  type Permission,
} from '../../api/permissions';

const ROLES: Permission['role'][] = ['reader', 'developer', 'maintainer', 'project_admin', 'org_admin'];

const selectClass =
  'h-10 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function roleTone(r: Permission['role']): 'accent' | 'nebula' | 'warning' | 'danger' | 'neutral' {
  switch (r) {
    case 'org_admin': return 'danger';
    case 'project_admin': return 'warning';
    case 'maintainer': return 'nebula';
    case 'developer': return 'accent';
    case 'reader': return 'neutral';
  }
}

export function AdminPermissions() {
  const { data: projects } = useProjects();
  const [filter, setFilter] = useState<string>('');
  const { data: perms, mutate } = usePermissions(filter || undefined);
  const [target, setTarget] = useState<'user' | 'group'>('user');
  const [identity, setIdentity] = useState('');
  const [project, setProject] = useState<string>('');
  const [role, setRole] = useState<Permission['role']>('developer');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onCreate = async () => {
    setBusy(true);
    setErr(null);
    try {
      await createPermission({
        project_id: project || null,
        user_email: target === 'user' ? identity : undefined,
        group_name: target === 'group' ? identity : undefined,
        role,
      });
      setIdentity('');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Remove this permission?')) return;
    await deletePermission(id);
    await mutate();
  };

  return (
    <AdminShell
      title="Permissions"
      description="Bind users or groups to a project (or org-wide) with a specific role. Roles: reader (read), developer (read+write artifacts), maintainer (manage repos), project_admin (project-scoped admin), org_admin (everything)."
    >
      <Card>
        <CardHeader title="Add binding" description="Pick a target type, enter their identifier, choose a project and role." />
        <CardBody className="space-y-4">
          <div>
            <label id="perm-target-label" className="block text-sm font-medium text-fg mb-1.5">Target</label>
            <div className="flex gap-2" role="group" aria-labelledby="perm-target-label">
              {(['user', 'group'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTarget(t)}
                  className={
                    'inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm border transition-colors ' +
                    (target === t
                      ? 'border-nebula text-nebula bg-nebula/10'
                      : 'border-border text-fg-muted hover:text-fg')
                  }
                >
                  {t === 'user' ? <Users className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
                  {t === 'user' ? 'User (email)' : 'Group (name)'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label htmlFor="perm-identity" className="block text-sm font-medium text-fg mb-1.5">
              {target === 'user' ? 'Email' : 'Group name'}
            </label>
            <Input
              id="perm-identity"
              value={identity}
              onChange={(e) => setIdentity(e.target.value)}
              placeholder={target === 'user' ? 'alice@example.com' : 'devops'}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="perm-project" className="block text-sm font-medium text-fg mb-1.5">Project (optional — empty = org-wide)</label>
              <select id="perm-project" className={selectClass + ' w-full'} value={project} onChange={(e) => setProject(e.target.value)}>
                <option value="">— org-wide —</option>
                {projects?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="perm-role" className="block text-sm font-medium text-fg mb-1.5">Role</label>
              <select
                id="perm-role"
                className={selectClass + ' w-full'}
                value={role}
                onChange={(e) => setRole(e.target.value as Permission['role'])}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {err && <p className="text-danger font-mono text-sm">{err}</p>}
        </CardBody>
        <CardFooter className="flex justify-end">
          <Button variant="primary" disabled={busy || !identity.trim()} onClick={onCreate}>
            <Plus className="h-4 w-4" />
            Add binding
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader
          title={`Bindings (${perms?.length ?? 0})`}
          description="Org-wide rows (no project) appear first."
          actions={
            <select className={selectClass} value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">All projects</option>
              {projects?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Scope</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Target</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Role</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {perms?.length ? (
                perms.map((p) => (
                  <tr key={p.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                    <td className="px-5 py-3 text-fg-muted text-xs">
                      {p.project_slug ? (
                        <span className="orb-code">{p.project_slug}</span>
                      ) : (
                        <Badge tone="warning">org-wide</Badge>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-fg">{p.user_email || p.group_name}</span>
                      <span className="text-fg-subtle text-xs ml-2">
                        {p.user_email ? 'user' : 'group'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <Badge tone={roleTone(p.role)}>{p.role}</Badge>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => onDelete(p.id)}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-fg-muted text-sm">
                    No bindings yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminShell>
  );
}
